import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { authApi } from '@/lib/api';
import { toast } from 'sonner';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [linkInvalid, setLinkInvalid] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Le mot de passe doit faire au moins 8 caractères.');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas.');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.resetPassword({ password });
      toast.success('Mot de passe réinitialisé avec succès !');
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('Token') || detail.includes('expir') || detail.includes('invalide')) {
        setLinkInvalid(true);
      }
      toast.error(detail || 'Une erreur est survenue lors du changement.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-10">
        <div className="bg-white rounded-3xl p-10 shadow-lg border border-garden-200 max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <ShieldCheck size={48} className="text-garden-800" />
          </div>
          <h1 className="text-2xl font-heading font-bold text-garden-800 mb-2">Succès !</h1>
          <p className="text-sm text-charcoal/60 mb-6">Votre mot de passe a été modifié avec succès.</p>
          <Button
            onClick={() => navigate('/admin/login')}
            className="bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-base font-medium w-full"
          >
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  if (linkInvalid) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-10">
        <div className="bg-white rounded-3xl p-10 shadow-lg border border-red-200 max-w-md w-full text-center">
          <h1 className="text-xl font-heading font-bold text-red-600 mb-2">Erreur</h1>
          <p className="text-sm text-charcoal/60 mb-6">Le lien de réinitialisation est invalide ou a expiré.</p>
          <Button
            onClick={() => navigate('/admin/login')}
            className="bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-base font-medium w-full"
          >
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream dark:bg-[#121212] flex items-center justify-center px-4 py-10 transition-colors duration-200">
      <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl p-10 shadow-lg border border-garden-800/5 dark:border-zinc-800 max-w-md w-full">
        <div className="text-center">
          <span className="font-accent text-3xl text-garden-800 dark:text-sunny block mb-2">Côté Terrasse</span>
          <h1 className="text-2xl font-heading font-bold text-garden-800 dark:text-sunny mb-2">Nouveau mot de passe</h1>
          <p className="text-sm text-charcoal/60 dark:text-zinc-400 mb-6">
            Définissez votre nouveau mot de passe d'administration.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-sm dark:text-zinc-300">Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Au moins 8 caractères"
                className="border-garden-200 dark:border-zinc-800 rounded-xl mt-1 pr-10 dark:bg-zinc-900 dark:text-white"
              />
              <button
                type="button"
                onClick={() => setShowPwd((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-charcoal/40 dark:text-zinc-500 hover:text-charcoal/70"
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div>
            <Label className="text-sm dark:text-zinc-300">Confirmer le mot de passe</Label>
            <Input
              type={showPwd ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Saisissez à nouveau"
              className="border-garden-200 dark:border-zinc-800 rounded-xl mt-1 dark:bg-zinc-900 dark:text-white"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="bg-garden-800 hover:bg-garden-900 text-white rounded-full py-6 text-base font-medium w-full"
          >
            <Lock size={18} className="mr-2" />
            {submitting ? 'Enregistrement...' : 'Enregistrer le mot de passe'}
          </Button>
        </form>
      </div>
    </div>
  );
}
